@use '../../styles/sassHelpers';
@use '../../styles/colors';
@use '../../styles/fonts';
@use '../../styles/tokens';

.bpm-page {
  @include sassHelpers.fullBox;

  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  min-width: 700px;
  height: 100%;
  background-color: white;

  .error-boundary {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;

    @include fonts.heading3;
  }

  & > .header {
    // .bpm-page is a flex container, so .header (a flex item) forms its own stacking
    // context from this z-index alone — position doesn't need to be set. That context
    // caps EVERYTHING nested inside .header (any dropdown/menu, however high its own
    // z-index) at this value when compared against sibling flex items like .body. Since
    // FullPageSpinner and other "always on top" overlays
    // use the codebase's 10000000 "beat everything" convention and commonly render inside
    // .body while .header stays interactive (e.g. CrossChannel's KPI/Source dropdowns
    // during a background refetch), .header must clear that bar too, or an open dropdown
    // menu renders behind the loading spinner despite its own z-index.
    z-index: tokens.$z-page-header;
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    min-height: 48px;
    padding: tokens.$space-2 tokens.$space-6;
    border-bottom: tokens.$border-width solid tokens.$border-color;
    background-color: colors.$Brand80;

    .title-container {
      display: flex;
      flex-flow: row;
      align-items: center;
      gap: tokens.$space-4;
      height: sassHelpers.$title-container-height;

      .bliss-point-logo {
        width: auto;
        height: 38px;
        padding-bottom: 4px;
      }

      .divider {
        height: 32px;
        border-left: 2px solid white;
      }

      .title {
        @include fonts.heading3Bold;

        margin-right: tokens.$space-4;
        color: white;
      }

      .navs {
        flex-wrap: nowrap;
        gap: tokens.$space-3;
        white-space: nowrap;

        .page-nav-item {
          @include fonts.body;

          padding: tokens.$space-2 tokens.$space-1;
          text-decoration: none;
          color: colors.$Brand30;
          cursor: pointer;

          &.active {
            color: white;
          }
        }
      }
    }

    .actions {
      display: flex;
      flex: 1;
      flex-flow: row wrap;
      justify-content: flex-end;
      align-items: center;
      color: white;
    }
  }

  .page-sub-header {
    display: flex;
    flex-flow: row wrap;
    flex-direction: row;
    align-items: center;
    min-height: tokens.$control-height-lg + tokens.$space-2;
    padding: tokens.$space-1;
    border-bottom: tokens.$border-width solid tokens.$border-color;
    background-color: white;
  }

  // Portal target for page toolbars (PageToolbarSlot). Above `.body` so toolbar menus open
  // over page content.
  .page-toolbar-slot {
    position: relative;
    z-index: tokens.$z-dropdown;

    &:empty {
      display: none;
    }
  }

  & > .body {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: stretch;
    gap: tokens.$space-6;
    width: 100%;
    height: 100%;
    padding: tokens.$space-6;
    overflow: auto;
    background-color: sassHelpers.$redesign-background-color;
  }
}

// Metrics tables (Single Channel + Cross Channel) are full-bleed: the controls bar and the table
// sit flush against the page edges with no gutter or gap, like the legacy pages.
.bpm-page:has(.metrics-table-controls-container, .pacing-preset-table-dashboard) > .body {
  gap: 0;
  padding: tokens.$space-1 0 0;
  overflow: hidden;
}

// .bpm-page is `position: absolute; inset: 0` (sassHelpers.fullBox) sized to fill its
// containing block — it cannot grow to fit content, which is what a printed page needs.
// Combined with `& > .body`'s own height: 100% + overflow: auto, this is the same
// fixed-viewport pattern reset in App.scss, one level deeper. Undo both here so a page
// that prints (currently only Google Ads Audit's PDF export) flows and paginates
// naturally instead of being clipped to whatever fit on screen.
@media print {
  .bpm-page {
    position: static;
    inset: auto;
    min-width: 0;
    height: auto;

    & > .header,
    & > .page-sub-header {
      display: none;
    }

    & > .body {
      height: auto;
      overflow: visible;
    }
  }
}

.new-version-alert-bar {
  z-index: 101;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: tokens.$space-1 tokens.$space-2;
  border: none;
  border-bottom: tokens.$border-width solid colors.$Warning700;
  border-radius: 0;
  background-color: colors.$Warning300;
}

.bpm-nav-placeholder {
  width: 56px;
  padding: tokens.$space-2;
  color: white;
  background: colors.$Brand100;
}

// The confirm can open from the side nav, whose menus are popovers (`$zindex-popover`) that sit above
// Bootstrap's modal layer. Lift this dialog and its backdrop to the popover layer; they mount after
// the popover in <body>, so they paint over it and the nav stays open behind the backdrop.
.modal.are-you-sure-dialog,
.modal-backdrop.are-you-sure-backdrop {
  z-index: var(--zindex-popover);
}
